// Форматирует сумму в центах для отображения:
// целые суммы — без дробной части ($49), дробные — ровно 2 знака ($9.10).
//
// Устаревшее: делит на 100, а это верно только для доллара. У JPY сумма в API
// совпадает с ценой, у UGX отличается в сто раз. Для новых мест — formatMoney.
export const formatPrice = (cents) => {
  const value = cents / 100
  return Number.isInteger(value) ? `${value}` : value.toFixed(2)
}

export const DEFAULT_CURRENCY = 'usd'

/**
 * Локаль форматирования цены.
 *
 * Всегда английская, независимо от языка интерфейса. Причин две. Первая: цены
 * всегда печатались как `$12.99`, и переезд на `12,99 $` для немецкого
 * интерфейса был бы регрессией для тех, у кого валюта не менялась. Вторая:
 * суммы стоят внутри английских предложений («Then €10.99 weekly»), и
 * европейская постановка символа в них смотрится хуже, чем американская.
 *
 * От валюты это не зависит: символ и число знаков после запятой берутся из
 * кода валюты, а не из локали.
 */
const FORMAT_LOCALE = 'en'

/**
 * Сколько знаков после запятой в строке суммы.
 * '10.99' → 2, '2099' → 0.
 */
const decimalsIn = (amount) => {
  const dot = String(amount).indexOf('.')

  return dot === -1 ? 0 : String(amount).length - dot - 1
}

/**
 * Локальная цена тарифа или null, если сервер её не прислал.
 *
 * Отсутствие полей означает доллар: либо тариф не локализован (месячный,
 * годовой, разовый), либо страну не определили, либо это купонный тариф,
 * которому бэкенд отказывает в локализации.
 */
export const tariffPrice = (paymentType) => {
  if (!paymentType || !paymentType.currency || !paymentType.localizedAmount) {
    return null
  }

  return {
    amount: paymentType.localizedAmount,
    currency: paymentType.currency
  }
}

/**
 * Локальная цена первой (скидочной) фазы купонного тарифа или null.
 *
 * Отдельным полем, а не вычислением из полной цены: разрядность валюты из
 * строки не выводится — у круглых '40' (евро) и '6000' (исландские кроны)
 * знаков после запятой нет одинаково, а скидочная цена у первого '3.49',
 * у второго '489'. Обе строки приходят с бэкенда как есть.
 *
 * Null означает доллар: либо тариф без купона, либо скидку в этой валюте
 * не заливали — тогда бэкенд не отдаёт и полную локальную цену.
 */
export const discountedTariffPrice = (paymentType) => {
  if (!paymentType || !paymentType.currency || !paymentType.localizedDiscountedAmount) {
    return null
  }

  return {
    amount: paymentType.localizedDiscountedAmount,
    currency: paymentType.currency
  }
}

/**
 * Сумма с символом валюты: '€10.99', '¥2,099', '$12.99'.
 *
 * Число берётся разбором строки, а не делением минорных единиц на сто —
 * последнее верно только пока валюта одна.
 *
 * Число знаков после запятой берётся из прайса, а не из правил валюты.
 * Круглая цена записана в нём как '40' и остаётся круглой: '€40', а не
 * '€40.00' — ровно как доллар печатается '$49'. Так заданы полные цены
 * купонных тарифов, у всех 117 валют с дробной частью.
 *
 * Если же знаков в прайсе больше, чем допускает валюта, возвращается
 * исходная строка с кодом валюты: Intl округлил бы сумму, а показанное
 * обязано совпадать со списанным.
 */
export const formatMoney = (amount, currency) => {
  const code = String(currency || DEFAULT_CURRENCY).toUpperCase()
  const text = String(amount)
  const value = Number(text)

  if (!Number.isFinite(value)) {
    return text
  }

  try {
    const formatter = new Intl.NumberFormat(FORMAT_LOCALE, {
      style: 'currency',
      currency: code
    })

    const fraction = formatter.formatToParts(value).find(part => part.type === 'fraction')
    const currencyDecimals = fraction ? fraction.value.length : 0
    const priceDecimals = decimalsIn(text)

    if (priceDecimals > currencyDecimals) {
      return `${text} ${code}`
    }

    if (priceDecimals === currencyDecimals) {
      return formatter.format(value)
    }

    return new Intl.NumberFormat(FORMAT_LOCALE, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: priceDecimals,
      maximumFractionDigits: priceDecimals
    }).format(value)
  } catch (error) {
    // Экзотический код валюты или среда без данных о ней. Показать сумму
    // с кодом честнее, чем уронить экран оплаты.
    return `${text} ${code}`
  }
}

/**
 * Отформатированная цена тарифа для листовых компонентов.
 *
 * Долларовый путь намеренно не идёт через Intl.
 *
 * Раньше словарь печатал `$` + formatPrice(cents), а formatPrice по правилу
 * «круглая сумма — без копеек» даёт `$49`, `$79`, `$1`. Правило общее и
 * действует для любого тарифа, а не только для годового. Intl такую сумму
 * записал бы как `$79.00` — то есть изменил бы вид цены всем нынешним
 * пользователям, которым локализация ничего не даёт.
 *
 * Поэтому доллар воспроизводится байт в байт, а Intl отвечает только за
 * валюты, которых раньше не было.
 *
 * @param price локальная цена из tariffPrice() или null
 * @param cents долларовая сумма в центах — то, что приходило раньше
 */
export const formatTariffPrice = (price, cents) => {
  if (price) {
    return formatMoney(price.amount, price.currency)
  }

  return `$${formatPrice(cents)}`
}

/**
 * Круглая сумма в том же виде, в каком показываются остальные суммы тарифа.
 *
 * Тексты про триал печатали литеральные `$0.00` и `$1.00`. Рядом с локальной
 * ценой долларовый ноль выглядит ошибкой, а для иены — ещё и лишними знаками.
 *
 * @param whole целая часть строкой: '0' для нулевой стоимости триала,
 *              '1' для суммы, которой банк держит авторизацию карты
 */
export const formatLiteralLike = (price, whole) => {
  if (!price) {
    // Ровно то, что печаталось в словаре литералом.
    return `$${whole}.00`
  }

  const amount = decimalsIn(price.amount) === 0 ? whole : `${whole}.00`

  return formatMoney(amount, price.currency)
}

/**
 * Ноль в валюте тарифа: '$0.00', '€0.00', '¥0'.
 */
export const formatZeroLike = (price) => formatLiteralLike(price, '0')

/**
 * Набор денежных подстановок для текстов пейволла.
 *
 * Собран в одном месте, потому что нужен во всех четырёх вариантах smallprint
 * и в попапе карты. Раздать его по компонентам значит однажды забыть где-то
 * `zero` и показать человеку буквальное `{{zero}}` на экране оплаты.
 */
export const moneyVars = (price, cents) => ({
  amount: formatTariffPrice(price, cents),
  zero: formatLiteralLike(price, '0'),
  authHold: formatLiteralLike(price, '1'),
  currency: currencyCode(price)
})

/**
 * Код валюты для юридических текстов: 'USD', 'EUR'.
 *
 * Smallprint утверждает, в какой валюте обрабатываются платежи, и это
 * заявление обязано быть правдой.
 */
export const currencyCode = (price) =>
  String(price ? price.currency : DEFAULT_CURRENCY).toUpperCase()

/**
 * Валюта, в которой пользователю показали этот тариф.
 *
 * Уходит в теле subscribe: списывать надо в той валюте, которую человек видел
 * в листе кошелька. Сервер к моменту оформления может определить другую —
 * достаточно включить VPN между показом пейволла и нажатием кнопки, — и без
 * этого поля списание разойдётся с показанным.
 *
 * null означает доллар: сервер тогда решает сам, как и раньше.
 */
export const shownCurrency = (paymentState, paymentTypeId) => {
  if (!paymentState || paymentTypeId === undefined || paymentTypeId === null) {
    return null
  }

  const wallets = [paymentState.aPay, paymentState.gPay]
  // Ключи перечислены явно: Object.entries в этой сборке Babel разворачивается
  // в хелпер, который ломается на неитерабельном значении (см. CLAUDE.md).
  const tariffs = ['oneTimePay', 'weeklySub', 'monthlySub', 'annualSub']

  for (const wallet of wallets) {
    if (!wallet) {
      continue
    }

    for (const key of tariffs) {
      const tariff = wallet[key]

      if (tariff && String(tariff.id) === String(paymentTypeId)) {
        const price = tariffPrice(tariff)

        return price ? price.currency : null
      }
    }
  }

  return null
}
